import apis from '../HttpConfig/Api';

class BatchedLogger {
    constructor() {
        if (BatchedLogger.instance) {
            return BatchedLogger.instance;
        }

        // Check if CloudWatch logging should be enabled based on REACT_APP_ENABLE_LOGGING
        // In React, environment variables are automatically loaded and prefixed with REACT_APP_
        this.isCloudWatchEnabled = process.env.REACT_APP_ENABLE_LOGGING === 'true';

        console.log(`[BatchedLogger] REACT_APP_ENABLE_LOGGING: ${process.env.REACT_APP_ENABLE_LOGGING}`);
        console.log(`[BatchedLogger] CloudWatch logging: ${this.isCloudWatchEnabled ? 'ENABLED' : 'DISABLED'}`);

        this.logs = [];
        this.batchSize = 20; // Hardcoded: increased batch size to reduce frequency
        this.flushInterval = 60000; // Hardcoded: 60 seconds (reduced from 5 seconds)
        this.isFlushingInProgress = false;
        this.maxRetries = 3;
        this.retryDelay = 1000;

        // Start auto-flush timer only if CloudWatch is enabled
        if (this.isCloudWatchEnabled) {
            this.startAutoFlush();
            console.log('[BatchedLogger] Auto-flush timer started');
        } else {
            console.log('[BatchedLogger] CloudWatch logging disabled, using console logging only');
        }

        // Flush logs before page unload only if CloudWatch is enabled
        if (this.isCloudWatchEnabled) {
            window.addEventListener('beforeunload', () => {
                this.flush(true);
            });
        }

        BatchedLogger.instance = this;
    }

    generateSessionId() {
        const sessionId = sessionStorage.getItem('batchedLoggerSessionId') ||
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);

        if (!sessionStorage.getItem('batchedLoggerSessionId')) {
            sessionStorage.setItem('batchedLoggerSessionId', sessionId);
        }

        return sessionId;
    }

    getBrowserName() {
        const userAgent = navigator.userAgent;
        if (userAgent.includes('Chrome')) return 'Chrome';
        if (userAgent.includes('Firefox')) return 'Firefox';
        if (userAgent.includes('Safari')) return 'Safari';
        if (userAgent.includes('Edge')) return 'Edge';
        if (userAgent.includes('Opera')) return 'Opera';
        return 'Unknown';
    }

    getBrowserVersion() {
        const userAgent = navigator.userAgent;
        const match = userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)\/(\d+)/);
        return match ? match[2] : 'Unknown';
    }

    startAutoFlush() {
        setInterval(() => {
            this.flush();
        }, this.flushInterval);
    }

    createLogEntry(level, message, context = {}) {
        return {
            level,
            message: `[FRONTEND] ${message}`, // Prefix all frontend messages
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            logOrigin: 'client-side',
            ...context
        };
    }

    addLog(level, message, context = {}) {
        // Hardcoded log level filtering - minimum level: info
        const minLogLevel = 'info';
        const logLevels = { debug: 0, info: 1, warn: 2, error: 3, critical: 4 };

        if (logLevels[level] < logLevels[minLogLevel]) {
            return; // Skip logging if below minimum level
        }

        const logEntry = this.createLogEntry(level, message, context);

        // Always log to console in development, or all environments if CloudWatch is disabled
        // In React, NODE_ENV is automatically set by Create React App
        if (process.env.NODE_ENV === 'development' || !this.isCloudWatchEnabled) {
            const consoleMethod = this.getConsoleMethod(level);
            consoleMethod(`[${level.toUpperCase()}] ${message}`, context);
        }

        // Only add to logs buffer if CloudWatch is enabled
        if (this.isCloudWatchEnabled) {
            this.logs.push(logEntry);

            // Auto-flush if batch size reached
            if (this.logs.length >= this.batchSize) {
                this.flush();
            }
        }
    }

    getConsoleMethod(level) {
        switch (level) {
            case 'error':
                return console.error;
            case 'warn':
                return console.warn;
            case 'info':
                return console.info;
            case 'debug':
                return console.log;
            default:
                return console.log;
        }
    }

    async flush(immediate = false) {
        // Skip flushing if CloudWatch is disabled or no logs to send
        if (!this.isCloudWatchEnabled || this.logs.length === 0 || (this.isFlushingInProgress && !immediate)) {
            return;
        }

        this.isFlushingInProgress = true;
        const logsToSend = [...this.logs];
        this.logs = [];

        try {
            await this.sendLogs(logsToSend);
        } catch (error) {
            // Re-add logs to buffer for retry, but limit buffer size
            if (this.logs.length < 100) {
                this.logs.unshift(...logsToSend);
            }
            console.error('Failed to send logs:', error);
        } finally {
            this.isFlushingInProgress = false;
        }
    }

    async sendLogs(logs, retryCount = 0) {
        try {
            // Use frontend-specific endpoint to separate log streams
            await apis.instance.post('/log-batch-frontend', { logs });
        } catch (error) {
            if (retryCount < this.maxRetries) {
                await this.delay(this.retryDelay * Math.pow(2, retryCount));
                return this.sendLogs(logs, retryCount + 1);
            }
            throw error;
        }
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // Public API methods
    info(message, context = {}) {
        this.addLog('info', message, context);
    }

    warn(message, context = {}) {
        this.addLog('warn', message, context);
    }

    error(message, context = {}) {
        this.addLog('error', message, context);
    }

    debug(message, context = {}) {
        this.addLog('debug', message, context);
    }

    // Helper methods for specific logging scenarios
    logUserAction(action, details = {}) {
        this.warn(`User Action: ${action}`, {
            category: 'user_action',
            action,
            source: 'frontend',
            logType: 'user_interaction',
            ...details
        });
    }

    logApiCall(method, url, status, duration, details = {}) {
        const level = status >= 400 ? 'error' : 'info';
        this.addLog(level, `API Call: ${method} ${url}`, {
            category: 'api_call',
            method,
            url,
            status,
            duration,
            source: 'frontend',
            logType: 'api_interaction',
            ...details
        });
    }

    logComponentMount(componentName, props = {}) {
        this.warn(`Component Mounted: ${componentName}`, {
            category: 'component_lifecycle',
            component: componentName,
            props: Object.keys(props),
            source: 'frontend',
            logType: 'react_lifecycle'
        });
    }

    logComponentUnmount(componentName) {
        this.warn(`Component Unmounted: ${componentName}`, {
            category: 'component_lifecycle',
            component: componentName,
            source: 'frontend',
            logType: 'react_lifecycle'
        });
    }

    logError(error, context = {}) {
        this.error(`Application Error: ${error.message}`, {
            category: 'app_error',
            stack: error.stack,
            name: error.name,
            source: 'frontend',
            logType: 'error_handling',
            ...context
        });
    }

    logPerformance(metric, value, context = {}) {
        this.info(`Performance: ${metric}`, {
            category: 'performance',
            metric,
            value,
            source: 'frontend',
            logType: 'performance_metric',
            ...context
        });
    }

    // Force flush all pending logs
    async forceFlush() {
        await this.flush(true);
    }

    // Get current log buffer size
    getBufferSize() {
        return this.logs.length;
    }

    // Clear all pending logs
    clearBuffer() {
        this.logs = [];
    }
}

// Create and export singleton instance
const logger = new BatchedLogger();

export default logger;
